const APP_PRODUCTION_HOST = 'app.vetsi.pl';
const GA4_MEASUREMENT_ID = 'G-BG0Y2J6NDZ';
const ANALYTICS_CONSENT_EVENT = 'vetsi:analytics-consent';

type AnalyticsEvent =
  | {
      name: 'form_start';
      params: { form_id: 'professional_registration' };
    }
  | {
      name: 'sign_up';
      params: { method: 'professional_registration' };
    };

interface Gtag {
  (
    command: 'event',
    eventName: AnalyticsEvent['name'],
    params: Record<string, string>,
  ): void;
  (command: 'set', params: { user_id: string | null }): void;
}

export interface AnalyticsTransport {
  hostname: string;
  gtag?: Gtag;
  statisticsConsent?: boolean;
}

export interface ProfessionalRegistrationAnalytics {
  trackFormStart: () => void;
  trackSignUp: (uid?: string) => void;
}

declare global {
  interface Window {
    gtag?: Gtag;
    __VETSI_STATISTICS_CONSENT__?: boolean;
  }
}

const getBrowserTransport = (): AnalyticsTransport | null => {
  if (typeof window === 'undefined') return null;

  return {
    hostname: window.location.hostname,
    gtag: window.gtag,
    statisticsConsent: window.__VETSI_STATISTICS_CONSENT__ === true,
  };
};

let authenticatedUid: string | null = null;
let appliedUid: string | null = null;

const applyAnalyticsUserId = (
  uid: string | null,
  transport: AnalyticsTransport | null,
): boolean => {
  if (
    !transport ||
    transport.hostname !== APP_PRODUCTION_HOST ||
    !transport.gtag
  )
    return false;
  const consentedUid = transport.statisticsConsent === true ? uid : null;
  if (appliedUid === consentedUid) return true;

  try {
    transport.gtag('set', { user_id: consentedUid });
    appliedUid = consentedUid;
    return true;
  } catch {
    return false;
  }
};

export const setAnalyticsUserId = (uid: string | null): void => {
  authenticatedUid = uid;
  applyAnalyticsUserId(uid, getBrowserTransport());
};

export const startAnalyticsConsentBridge = (): (() => void) => {
  const onConsent = (): void => {
    applyAnalyticsUserId(authenticatedUid, getBrowserTransport());
  };
  window.addEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
  onConsent();
  return () => {
    window.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
    setAnalyticsUserId(null);
  };
};

export const sendAnalyticsEvent = (
  event: AnalyticsEvent,
  transport: AnalyticsTransport | null = getBrowserTransport(),
): boolean => {
  if (
    !transport ||
    transport.hostname !== APP_PRODUCTION_HOST ||
    !transport.gtag
  )
    return false;

  try {
    transport.gtag('event', event.name, {
      ...event.params,
      send_to: GA4_MEASUREMENT_ID,
    });
    return true;
  } catch {
    return false;
  }
};

export const createProfessionalRegistrationAnalytics = (
  transport?: AnalyticsTransport | null,
): ProfessionalRegistrationAnalytics => {
  let formStarted = false;
  let signedUp = false;
  const currentTransport = (): AnalyticsTransport | null =>
    transport === undefined ? getBrowserTransport() : transport;

  return {
    trackFormStart: (): void => {
      if (formStarted) return;
      formStarted = sendAnalyticsEvent(
        {
          name: 'form_start',
          params: { form_id: 'professional_registration' },
        },
        currentTransport(),
      );
    },
    trackSignUp: (uid?: string): void => {
      if (signedUp) return;
      // Signup is a past action: granting consent later must not replay it.
      signedUp = true;
      const activeTransport = currentTransport();
      const signupUid = typeof uid === 'string' && uid.length > 0 ? uid : null;
      if (!applyAnalyticsUserId(signupUid, activeTransport)) return;
      sendAnalyticsEvent(
        { name: 'sign_up', params: { method: 'professional_registration' } },
        activeTransport,
      );
      // Professional signup does not sign the browser into the new account.
      applyAnalyticsUserId(authenticatedUid, activeTransport);
    },
  };
};
